import { useState } from 'react'
import { useNavigate } from 'react-router'
import { ChevronDown, X } from 'lucide-react'
import { type PublishablePlatformType, type Account } from '@backend/lib/db/schema/platform'
import { type SelectedAccount } from '../workspace-header/workspace-header-social-accounts'
import { InstagramIcon } from '@/components/ui/icons/InstagramIcon'
import { LinkedInIcon } from '@/components/ui/icons/LinkedInIcon'
import { FacebookIcon, TikTokIcon, YoutubeIcon } from '@/components/ui/icons/icons-new'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu'
import { useTheme } from '@/context/themeContext'
import { cn } from '@/lib/utils'
import {
  type PlatformAccountsMap,
  type PlatformAuthMap,
  getPlatformName,
} from '@/lib/workspace-preview-validation'

interface Props {
  selectedPlatform: PublishablePlatformType
  selectedAccounts: SelectedAccount[]
  onAccountsChange: (accounts: SelectedAccount[]) => void
  accounts: PlatformAccountsMap
  isAuthenticated: PlatformAuthMap
}

export const WorkspacePreviewAccountSelection = ({
  selectedPlatform,
  selectedAccounts,
  onAccountsChange,
  accounts,
  isAuthenticated,
}: Props) => {
  const { theme } = useTheme()
  const navigate = useNavigate()
  const [openAccountDropdown, setOpenAccountDropdown] = useState(false)

  const getPlatformIcon = (platform: PublishablePlatformType): React.ReactNode => {
    switch (platform) {
      case 'instagram':
        return <InstagramIcon width="14" height="14" />
      case 'linkedin':
        return <LinkedInIcon width="14" height="14" />
      case 'tiktok':
        return (
          <TikTokIcon width={14} height={14} color={theme === 'dark' ? '#FFFFFF' : '#000000'} />
        )
      case 'youtube':
        return (
          <YoutubeIcon width={14} height={14} color={theme === 'dark' ? '#FFFFFF' : '#FF0000'} />
        )
      case 'facebook':
        return <FacebookIcon width={14} height={14} color="#1877F2" />
    }
  }

  const getPlatformConfig = (platform: PublishablePlatformType) => ({
    accounts: accounts[platform],
    isAuthenticated: isAuthenticated[platform],
    icon: getPlatformIcon(platform),
    name: getPlatformName(platform),
  })

  const getSelectedAccount = (platform: PublishablePlatformType) => {
    return selectedAccounts.find((acc) => acc.platform === platform)
  }

  const handleSelectAccount = (platform: PublishablePlatformType, account: Account) => {
    const filteredAccounts = selectedAccounts.filter((acc) => acc.platform !== platform)

    const newAccount: SelectedAccount = {
      platform,
      accountId: account.id,
      displayName: account.displayName,
      imageUrl: account.imageUrl,
    }

    onAccountsChange([...filteredAccounts, newAccount])
    setOpenAccountDropdown(false)
  }

  const handleRemoveAccount = (platform: PublishablePlatformType) => {
    onAccountsChange(selectedAccounts.filter((acc) => acc.platform !== platform))
  }

  const config = getPlatformConfig(selectedPlatform)
  const selectedAccount = getSelectedAccount(selectedPlatform)

  return (
    <div className="mx-auto w-full max-w-[400px] sm:max-w-[450px]">
      {config.isAuthenticated && config.accounts.length > 0 ? (
        <div className="flex items-center gap-2">
          {selectedAccount ? (
            <DropdownMenu open={openAccountDropdown} onOpenChange={setOpenAccountDropdown}>
              <DropdownMenuTrigger asChild>
                <button className="border-border bg-card hover:bg-hover text-foreground flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs transition-colors">
                  {selectedAccount.imageUrl ? (
                    <img
                      src={selectedAccount.imageUrl}
                      alt={selectedAccount.displayName}
                      className="h-4 w-4 rounded-full object-cover"
                    />
                  ) : (
                    config.icon
                  )}
                  <span className="max-w-[120px] truncate">{selectedAccount.displayName}</span>
                  {config.accounts.length > 1 && <ChevronDown className="size-4 shrink-0" />}
                </button>
              </DropdownMenuTrigger>
              {config.accounts.length > 1 && (
                <DropdownMenuContent
                  className={cn('w-[200px]', theme === 'dark' ? 'dark' : '')}
                  side="bottom"
                  align="start"
                  sideOffset={8}>
                  {config.accounts.map((account) => (
                    <DropdownMenuItem
                      key={account.id}
                      onClick={() => handleSelectAccount(selectedPlatform, account)}
                      className="flex cursor-pointer items-center gap-2 p-2">
                      {account.imageUrl ? (
                        <img
                          src={account.imageUrl}
                          alt={account.displayName}
                          className="h-5 w-5 rounded-full object-cover"
                        />
                      ) : (
                        config.icon
                      )}
                      <span className="truncate text-xs font-medium">{account.displayName}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              )}
            </DropdownMenu>
          ) : (
            <DropdownMenu open={openAccountDropdown} onOpenChange={setOpenAccountDropdown}>
              <DropdownMenuTrigger asChild>
                <button className="border-border bg-card hover:bg-hover text-foreground flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs transition-colors">
                  {config.icon}
                  <span>Select account</span>
                  {config.accounts.length > 1 && <ChevronDown className="size-4 shrink-0" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className={cn('w-[200px]', theme === 'dark' ? 'dark' : '')}
                side="bottom"
                align="start"
                sideOffset={8}>
                {config.accounts.map((account) => (
                  <DropdownMenuItem
                    key={account.id}
                    onClick={() => handleSelectAccount(selectedPlatform, account)}
                    className="flex cursor-pointer items-center gap-2 p-2">
                    {account.imageUrl ? (
                      <img
                        src={account.imageUrl}
                        alt={account.displayName}
                        className="h-5 w-5 rounded-full object-cover"
                      />
                    ) : (
                      config.icon
                    )}
                    <span className="truncate text-xs font-medium">{account.displayName}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {selectedAccount && (
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleRemoveAccount(selectedPlatform)
              }}
              className="text-foreground hover:bg-hover flex h-6 w-6 items-center justify-center rounded-full transition-colors hover:text-red-500">
              <X className="size-3" />
            </button>
          )}
        </div>
      ) : (
        <button
          onClick={() => navigate('/account/social-media')}
          className="border-border bg-card hover:bg-hover text-foreground flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs transition-colors">
          {config.icon}
          <span>Connect {config.name}</span>
        </button>
      )}
    </div>
  )
}
